import * as Sentry from '@sentry/react'

import {
  eventInsightsResponseSchema,
  campaignResultsResponseSchema,
  insightsBreakdownResponseSchema,
  topPerformingAdsResponseSchema,
} from './schemas'

// v4 GET /insights -> FE domain model. reach for platform=google is the
// true campaign-level unique-users figure from the Google Ads API — pass it
// through as-is, don't run it through any per-day-summing logic that may
// exist for Meta elsewhere in the app.
export function mapEventInsights(data) {
  const parsed = eventInsightsResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = parsed.success ? parsed.data : data

  return {
    adSpend: source.ad_spend ?? null,
    reach: source.reach ?? null,
    impressions: source.impressions ?? null,
    clicks: source.clicks ?? null,
    ctr: source.ctr ?? null,
    start: source.start ?? null,
    end: source.end ?? null,
    userActionRequired: source.user_action_required ?? null,
  }
}

// Treat an all-null-fields object as equivalent to null so FE callers get
// the clean "no series data" signal the docs promised, rather than leaking
// this serialization artifact.
function mapSingleCampaignResult(result) {
  if (!result) return null
  const { total_transactions, total_revenue, roas, cpo } = result
  if (total_transactions == null && total_revenue == null && roas == null && cpo == null) {
    return null
  }
  return {
    totalTransactions: total_transactions ?? null,
    totalRevenue: total_revenue ?? null,
    roas: roas ?? null,
    cpo: cpo ?? null,
  }
}

// v4 GET /campaign_results -> FE domain model. seriesResults stays null
// unless the event is a series under FD-live attribution. platformAttribution
// only carries keys for the requested platform(s) (no empty meta/google keys)
// and is null wholesale when no attribution configuration was found.
export function mapCampaignResults(data) {
  const parsed = campaignResultsResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = parsed.success ? parsed.data : data

  return {
    eventResults: mapSingleCampaignResult(source.event_results),
    seriesResults: mapSingleCampaignResult(source.series_results),
    platformAttribution: source.platform_attribution ?? null,
  }
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

function mapBreakdownItem(item) {
  return {
    id: item.id ?? null,
    platform: item.platform ?? null,
    name: item.name,
    status: item.status ?? null,
    spend: toNumberOrNull(item.spend),
    impressions: toNumberOrNull(item.impressions),
    clicks: toNumberOrNull(item.clicks),
    reach: toNumberOrNull(item.reach),
    ctr: toNumberOrNull(item.ctr),
    purchase: toNumberOrNull(item.purchase),
    revenue: toNumberOrNull(item.revenue),
    roi: toNumberOrNull(item.roi),
    cpo: toNumberOrNull(item.cpo),
    rank: toNumberOrNull(item.rank),
  }
}

export function mapInsightsBreakdown(data) {
  const parsed = insightsBreakdownResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = parsed.success ? parsed.data : data

  return {
    items: (source.items || []).map(mapBreakdownItem),
    total: source.total ?? 0,
    limit: source.limit ?? 0,
    page: source.page ?? 1,
    pages: source.pages ?? 1,
    nextPage: source.nextPage ?? null,
    prevPage: source.prevPage ?? null,
  }
}

// Normalizes a per-platform top-ad item onto the fields that genuinely
// overlap between Meta and Google (id/name/tc/headline/body/media/
// destinationUrl/callToAction per the guide's example payloads) — fields one
// platform doesn't provide (Meta: adScore; Google: tc/body/callToAction)
// stay null rather than being invented.
function normalizeTopAd(item, platform) {
  return {
    platform,
    id: item.id,
    name: item.name,
    tc: item.tc ?? null,
    headline: item.headline ?? null,
    body: item.body ?? null,
    description: item.description ?? null,
    longHeadline: item.long_headline ?? null,
    media: item.media ?? null,
    destinationUrl: item.destination_url ?? null,
    callToAction: item.call_to_action ?? null,
    adScore: item.ad_score ?? null,
    adType: item.ad_type ?? null,
    businessName: item.business_name ?? null,
    logo: item.logo ?? null,
    displayUrlPath: item.display_url_path ?? null,
    companionBanner: item.companion_banner ?? null,
  }
}

// v4 GET /insights/top-performing-ads -> FE domain model. Only the requested
// platform keys are present on the result (a ?platform=google request has no
// `meta` key at all, matching the wire shape) — callers should check for key
// presence, not assume an empty array.
export function mapTopPerformingAds(data) {
  const parsed = topPerformingAdsResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = parsed.success ? parsed.data : data

  const result = {}
  if (source.meta) {
    result.meta = source.meta.map((item) => normalizeTopAd(item, 'meta'))
  }
  if (source.google) {
    result.google = source.google.map((item) => normalizeTopAd(item, 'google'))
  }
  return result
}

// Maps the raw v4 error body (unwrapEnvelope's `error` — {message, error_code,
// data?}, per app/core/errors/fd_abort) onto the same {message, code, status,
// data} shape the other v4 domain modules in this epic (e.g. runtimeBudget)
// already use — no new error-display convention introduced.
export function mapEventEvaluationError(error, status) {
  const body = error?.response?.data ?? error
  return {
    message: body?.message ?? body?.error_message ?? 'Unknown error',
    code: body?.error_code ?? body?.code ?? null,
    status: status ?? error?.response?.status ?? null,
    data: body?.data,
  }
}
